// pdfGenerator.js - PDF Generation Utility for Tenders and Quotations

import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Generate Tender Document PDF
export const generateTenderPDF = (tenderData, quotations = []) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;

  // Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('SWAMI VIVEKANANDA UNIVERSITY', pageWidth / 2, 25, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text('TENDER DOCUMENT', pageWidth / 2, 35, { align: 'center' });
  
  // Add horizontal line
  doc.setLineWidth(0.5);
  doc.line(margin, 45, pageWidth - margin, 45);
  
  let yPosition = 60;
  
  // Tender Details
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('TENDER DETAILS', margin, yPosition);
  yPosition += 15;
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const tenderDetails = [
    ['Tender ID:', tenderData.tender_id || 'TND-' + Date.now()],
    ['Title:', tenderData.title || ''],
    ['Department:', tenderData.department || ''],
    ['Category:', tenderData.procurement_category || ''],
    ['Tender Type:', tenderData.tender_type || ''],
    ['Estimated Value:', new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(tenderData.estimated_value || 0)],
    ['Submission Deadline:', new Date(tenderData.submission_deadline).toLocaleDateString('en-IN') || ''],
    ['Created Date:', new Date().toLocaleDateString('en-IN')],
  ];
  
  tenderDetails.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(value, margin + 50, yPosition);
    yPosition += 8;
  });
  
  yPosition += 10;
  
  // Description
  doc.setFont('helvetica', 'bold');
  doc.text('DESCRIPTION:', margin, yPosition);
  yPosition += 10;
  
  doc.setFont('helvetica', 'normal');
  const splitDescription = doc.splitTextToSize(tenderData.description || 'No description provided', pageWidth - 2 * margin);
  doc.text(splitDescription, margin, yPosition);
  yPosition += splitDescription.length * 6 + 15;
  
  // Terms and Conditions
  doc.setFont('helvetica', 'bold');
  doc.text('TERMS AND CONDITIONS:', margin, yPosition);
  yPosition += 10;
  
  const termsAndConditions = [
    '1. All bids must be submitted before the deadline mentioned above.',
    '2. Incomplete or late submissions will be rejected.',
    '3. The university reserves the right to accept or reject any bid without assigning reasons.',
    '4. Payment terms: 30 days from delivery and acceptance.',
    '5. Warranty period: As per industry standards (minimum 1 year).',
    '6. Delivery timeline must be clearly mentioned in the quotation.',
    '7. All prices should be inclusive of taxes and delivery charges.',
    '8. Technical specifications must be strictly adhered to.',
  ];
  
  doc.setFont('helvetica', 'normal');
  termsAndConditions.forEach(term => {
    const splitTerm = doc.splitTextToSize(term, pageWidth - 2 * margin);
    doc.text(splitTerm, margin, yPosition);
    yPosition += splitTerm.length * 6 + 3;
    
    // Add new page if needed
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 30;
    }
  });
  
  // Add quotations if provided
  if (quotations && quotations.length > 0) {
    doc.addPage();
    yPosition = 30;
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('GENERATED QUOTATIONS', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 20;
    
    quotations.forEach((quotation, index) => {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Quotation ${index + 1}: ${quotation.type}`, margin, yPosition);
      yPosition += 10;
      
      const quotationTable = [
        ['Item', 'Quantity', 'Unit Price', 'Total'],
        ...quotation.items.map(item => [
          item.description,
          item.quantity.toString(),
          new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(item.unitPrice),
          new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(item.total)
        ]),
        ['', '', 'Grand Total:', new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(quotation.grandTotal)]
      ];
      
      doc.autoTable({
        startY: yPosition,
        head: [quotationTable[0]],
        body: quotationTable.slice(1),
        margin: { left: margin, right: margin },
        styles: { fontSize: 9 },
        headStyles: { fillColor: [35, 133, 72] },
      });
      
      yPosition = doc.lastAutoTable.finalY + 15;
      
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 30;
      }
    });
  }
  
  // Footer
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
    doc.text('Generated by Swami Vivekananda University Procurement System', margin, doc.internal.pageSize.getHeight() - 10);
  }
  
  return doc;
};

// Generate Individual Quotation PDF
export const generateQuotationPDF = (quotationData, tenderData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;

  // Header
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('QUOTATION DOCUMENT', pageWidth / 2, 25, { align: 'center' });
  
  doc.setLineWidth(0.5);
  doc.line(margin, 35, pageWidth - margin, 35);
  
  let yPosition = 50;
  
  // Quotation Details
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('QUOTATION DETAILS', margin, yPosition);
  yPosition += 15;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const quotationDetails = [
    ['Quotation ID:', quotationData.quotation_id],
    ['Quotation Type:', quotationData.type],
    ['Tender ID:', tenderData.tender_id],
    ['Tender Title:', tenderData.title],
    ['Generated Date:', new Date().toLocaleDateString('en-IN')],
    ['Valid Until:', new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-IN')],
  ];
  
  quotationDetails.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(value, margin + 50, yPosition);
    yPosition += 8;
  });
  
  yPosition += 15;
  
  // Items Table
  doc.setFont('helvetica', 'bold');
  doc.text('ITEMS AND PRICING', margin, yPosition);
  yPosition += 10;
  
  const tableData = [
    ['S.No.', 'Description', 'Quantity', 'Unit Price', 'Total Amount'],
    ...quotationData.items.map((item, index) => [
      (index + 1).toString(),
      item.description,
      item.quantity.toString(),
      new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(item.unitPrice),
      new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(item.total)
    ])
  ];
  
  doc.autoTable({
    startY: yPosition,
    head: [tableData[0]],
    body: tableData.slice(1),
    margin: { left: margin, right: margin },
    styles: { fontSize: 9 },
    headStyles: { fillColor: [35, 133, 72] },
  });
  
  yPosition = doc.lastAutoTable.finalY + 20;
  
  // Total Summary
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const subtotal = quotationData.items.reduce((sum, item) => sum + item.total, 0);
  const tax = subtotal * 0.18; // 18% GST
  const grandTotal = subtotal + tax;
  
  doc.text('Subtotal:', pageWidth - margin - 60, yPosition);
  doc.text(new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(subtotal), pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 8;
  
  doc.text('GST (18%):', pageWidth - margin - 60, yPosition);
  doc.text(new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(tax), pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 8;
  
  doc.setFontSize(14);
  doc.text('Grand Total:', pageWidth - margin - 60, yPosition);
  doc.text(new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(grandTotal), pageWidth - margin, yPosition, { align: 'right' });
  
  // Footer
  doc.setFontSize(8);
  doc.text('This is a system-generated quotation document.', margin, doc.internal.pageSize.getHeight() - 20);
  doc.text(`Generated on ${new Date().toLocaleString('en-IN')}`, margin, doc.internal.pageSize.getHeight() - 10);
  
  return doc;
};

// Generate 3 Default Quotations for a Tender
export const generateDefaultQuotations = (tenderData) => {
  const basePrice = parseFloat(tenderData.estimated_value) || 100000;
  
  const quotationTypes = [
    {
      type: 'Basic Package',
      multiplier: 0.8,
      description: 'Standard specifications with basic features'
    },
    {
      type: 'Standard Package',
      multiplier: 1.0,
      description: 'Complete specifications as per tender requirements'
    },
    {
      type: 'Premium Package',
      multiplier: 1.3,
      description: 'Enhanced specifications with additional features'
    }
  ];
  
  return quotationTypes.map((type, index) => {
    const adjustedPrice = basePrice * type.multiplier;
    const itemPrice = adjustedPrice / 3; // Split into 3 items
    
    return {
      quotation_id: `QUO-${tenderData.tender_id || Date.now()}-${index + 1}`,
      tender_id: tenderData.tender_id,
      type: type.type,
      description: type.description,
      items: [
        {
          description: `${tenderData.title} - Primary Component`,
          quantity: 1,
          unitPrice: itemPrice,
          total: itemPrice
        },
        {
          description: `Installation and Setup`,
          quantity: 1,
          unitPrice: itemPrice * 0.3,
          total: itemPrice * 0.3
        },
        {
          description: `Maintenance and Support (1 Year)`,
          quantity: 1,
          unitPrice: itemPrice * 0.2,
          total: itemPrice * 0.2
        }
      ],
      grandTotal: adjustedPrice,
      status: 'generated',
      created_at: new Date().toISOString()
    };
  });
};

// Download PDF Function
export const downloadPDF = (doc, filename) => {
  try {
    doc.save(`${filename}.pdf`);
    return true;
  } catch (error) {
    console.error('Error downloading PDF:', error);
    return false;
  }
};

// Generate and Download Tender with Quotations
export const generateAndDownloadTenderPackage = (tenderData) => {
  try {
    // Generate quotations
    const quotations = generateDefaultQuotations(tenderData);
    
    // Generate main tender PDF
    const tenderPDF = generateTenderPDF(tenderData, quotations);
    downloadPDF(tenderPDF, `Tender_${tenderData.tender_id || Date.now()}`);
    
    // Generate individual quotation PDFs
    quotations.forEach((quotation, index) => {
      setTimeout(() => {
        const quotationPDF = generateQuotationPDF(quotation, tenderData);
        downloadPDF(quotationPDF, `${quotation.quotation_id}`);
      }, (index + 1) * 500); // Stagger downloads
    });
    
    return { success: true, quotations };
  } catch (error) {
    console.error('Error generating tender package:', error);
    return { success: false, error: error.message };
  }
};

// Utility to format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(amount || 0);
};

export default {
  generateTenderPDF,
  generateQuotationPDF,
  generateDefaultQuotations,
  downloadPDF,
  generateAndDownloadTenderPackage,
  formatCurrency
};